import {
  convertEnumValueToSlug,
  convertSlugToEnumValue,
} from 'lib/helpers/slugHelper';
import {
  buildQueryParamLink,
  BuildQueryParamLinkQueryParams,
} from 'lib/helpers/urlHelper';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { getVisibleTabOptions } from './helpers';
import { ITabOption } from './types';

export const useTabs = <T extends string>(params: {
  basePath: string;
  conditions: Record<T, boolean>;
  defaultTab: T;
  orderedTabOptions: ITabOption<T>[];
  queryParams?: BuildQueryParamLinkQueryParams;
  slug: string;
}): {
  activeTab: T;
  buildTabHref: (tab: T) => string;
  visibleTabOptions: ITabOption<T>[];
} => {
  const { replace } = useRouter();

  const visibleTabOptions = useMemo(() => {
    return getVisibleTabOptions(params.orderedTabOptions, params.conditions);
  }, [params.conditions, params.orderedTabOptions]);

  const requestedTab: T | null = useMemo(() => {
    return convertSlugToEnumValue(
      params.slug,
      _.mapValues(params.conditions, (_, key) => key as T)
    );
  }, [params.slug]);

  const activeTab: T = useMemo(() => {
    return _.some(visibleTabOptions, (x) => x.value === requestedTab)
      ? requestedTab
      : params.defaultTab;
  }, [visibleTabOptions, params.defaultTab, requestedTab]);

  const buildTabHref = (tab: T) => {
    return buildQueryParamLink(
      `${params.basePath}/${convertEnumValueToSlug(tab)}`,
      params.queryParams
    );
  };

  useEffect(() => {
    if (activeTab !== requestedTab) {
      replace(buildTabHref(activeTab));
    }
  }, [activeTab, requestedTab]);

  return {
    activeTab,
    buildTabHref,
    visibleTabOptions,
  };
};
